import { useEffect, useState } from 'react';

// Define types inline to avoid import issues
interface NewsletterSubscriptionResponse {
  success: boolean;
  message: string;
  contactId?: string;
}

interface UseNewsletterReturn {
  subscribe: (email: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  success: boolean;
  message: string | null;
}

export function useNewsletter(): UseNewsletterReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Reset success state after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
        setMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success]);

  const subscribe = async (email: string) => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    setMessage(null);

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data: NewsletterSubscriptionResponse = await response.json();

      if (data.success) {
        setSuccess(true);
        setMessage(data.message);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Newsletter subscription error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    subscribe,
    isLoading,
    error,
    success,
    message,
  };
} 